import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  Theme,
} from '@material-ui/core';
import * as React from 'react';
import { ReservationService } from 'services';
import { Reservation } from 'interfaces';

interface Props {
  reservation: Reservation | null;
  open: boolean;
  onClose: () => void;
  onCanceled: () => void;
}

/**
 * Cancel-reservation confirm dialog. Notify-customer checkbox defaults
 * to ON. When ticked, the API's `notify` flag is passed in the PUT, which
 * triggers ReservationMailer::canceledToCustomer.
 */
export const CancelReservationDialog: React.FC<Props> = ({
  reservation,
  open,
  onClose,
  onCanceled,
}) => {
  const [notify, setNotify] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const isMobile = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));

  React.useEffect(() => {
    if (open) {
      setNotify(true);
      setError(null);
    }
  }, [open]);

  if (!reservation) return null;

  const handleCancel = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await ReservationService.update(reservation.id, {
        status: 'canceled',
        notify: notify ? 1 : 0,
      });
      onCanceled();
    } catch (e) {
      setError((e as Error)?.message || 'Failed to cancel reservation');
    } finally {
      setSubmitting(false);
    }
  };

  const reservedAt = new Date(reservation.reservedAt);
  const reservedAtLabel = isNaN(reservedAt.getTime())
    ? '—'
    : reservedAt.toLocaleString();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      fullScreen={isMobile}
    >
      <DialogTitle>Cancel this reservation?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" style={{ marginBottom: 12 }}>
          <strong>{reservation.name}</strong> · {reservation.partySize}{' '}
          {reservation.partySize === 1 ? 'guest' : 'guests'}
          <br />
          {reservedAtLabel}
          {reservation.experience && (
            <>
              <br />
              Experience: <strong>{reservation.experience.title}</strong>
            </>
          )}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={notify}
              onChange={(_, v) => setNotify(v)}
              color="primary"
            />
          }
          label="Notify customer by email"
        />
        {notify && (
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: 'block', marginTop: 4 }}
          >
            A cancellation email will be sent to {reservation.email}.
          </Typography>
        )}
        {error && (
          <Typography
            color="error"
            variant="body2"
            style={{ marginTop: 12 }}
          >
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Keep reservation
        </Button>
        <Button
          onClick={handleCancel}
          color="secondary"
          variant="contained"
          disabled={submitting}
        >
          {submitting ? 'Cancelling…' : 'Cancel reservation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
